import * as T from 'three';

/** Align the authored flying silhouette with carried momentum. The pelvis stays
 * in place; banking, joint angles and the camera's anchor remain untouched. */
export class GlideAttitude {
  angle=0;
  pitch=NaN;
  private previousAuthored=NaN;
  private wasContinuous=false;
  private readonly pelvisPosition=new T.Vector3();
  private readonly axis=new T.Vector3();
  private readonly local=new T.Vector3();
  private readonly frame=new T.Quaternion();
  private readonly world=new T.Quaternion();
  private readonly parent=new T.Quaternion();
  private readonly delta=new T.Quaternion();
  /** Landing hand-off schedule: total span and the weight still to release. */
  private releaseSpan=0;
  private releaseLeft=1;

  reset():void {this.angle=0;this.pitch=this.previousAuthored=NaN;this.wasContinuous=false;this.releaseSpan=0;this.releaseLeft=1;}

  /** Hand the correction back to the authored pose over `remaining` seconds
   * (a dive landing). While airborne the visible inclination is held
   * continuous through the clip crossfade underneath, and the correction
   * decays so it is gone by touchdown: the body swings from head-down to
   * feet-first at a readable rate instead of snapping to the landing clip. */
  release(group:T.Object3D,bones:ReadonlyMap<string,T.Bone>,remaining:number):void {
    const pelvis=bones.get('pelvis'),chest=bones.get('spine_05');
    if(!pelvis?.parent||!chest)return;
    group.updateWorldMatrix(true,true);
    group.getWorldQuaternion(this.frame);
    pelvis.getWorldPosition(this.pelvisPosition);
    chest.getWorldPosition(this.local).sub(this.pelvisPosition).applyQuaternion(this.parent.copy(this.frame).invert());
    const authored=Math.atan2(this.local.y,-this.local.z);
    if(remaining>0&&Number.isFinite(this.previousAuthored)){
      const change=authored-this.previousAuthored;
      this.angle-=Math.atan2(Math.sin(change),Math.cos(change));
    }
    this.previousAuthored=authored;this.wasContinuous=false;
    // Smootherstep across the whole anticipation: the swing starts from rest,
    // peaks mid-flare and settles exactly as the feet arrive.
    if(this.releaseSpan<=0)this.releaseSpan=Math.max(remaining,.12);
    const u=Math.min(1,Math.max(0,1-remaining/this.releaseSpan)),left=1-u*u*u*(u*(u*6-15)+10);
    this.angle=left<=1e-6||this.releaseLeft<=1e-6?0:this.angle*left/this.releaseLeft;this.releaseLeft=left;
    this.pitch=Math.atan2(Math.sin(authored+this.angle),Math.cos(authored+this.angle));
    if(Math.abs(this.angle)<1e-4){this.angle=0;return;}
    this.axis.set(1,0,0).applyQuaternion(this.frame);
    this.delta.setFromAxisAngle(this.axis,this.angle);
    pelvis.getWorldQuaternion(this.world).premultiply(this.delta);
    pelvis.parent.getWorldQuaternion(this.parent).invert();
    pelvis.quaternion.copy(this.parent).multiply(this.world);
    pelvis.updateWorldMatrix(false,true);
  }

  update(dt:number,group:T.Object3D,bones:ReadonlyMap<string,T.Bone>,horizontal:number,vertical:number,weight:number,preserveAuthoredPitch=false,aim?:{pitch:number;limit?:number;continuous?:boolean}):void {
    this.releaseSpan=0;this.releaseLeft=1;
    const pelvis=bones.get('pelvis'),chest=bones.get('spine_05');
    if(!pelvis?.parent||!chest)return;
    group.updateWorldMatrix(true,true);
    group.getWorldQuaternion(this.frame);
    pelvis.getWorldPosition(this.pelvisPosition);
    chest.getWorldPosition(this.local).sub(this.pelvisPosition).applyQuaternion(this.parent.copy(this.frame).invert());
    const authored=Math.atan2(this.local.y,-this.local.z);
    if(this.wasContinuous&&!aim?.continuous)this.angle=Math.atan2(Math.sin(this.angle),Math.cos(this.angle));
    this.wasContinuous=!!aim?.continuous;
    // Directional blends change the authored inclination while its trajectory
    // correction is still easing. Rebase that correction on the incoming pose
    // so the two rotations do not add into an unintended backward somersault.
    if(preserveAuthoredPitch&&Number.isFinite(this.previousAuthored)){
      const change=authored-this.previousAuthored;
      this.angle-=Math.atan2(Math.sin(change),Math.cos(change));
    }
    this.previousAuthored=authored;
    // A small positive incidence keeps the chest lifted and feet trailing. At
    // the top of the arc this relaxes continuously back into ordinary gliding.
    const path=Math.atan2(vertical,Math.max(.01,Math.abs(horizontal)));
    const target=T.MathUtils.clamp(aim?.pitch??path+T.MathUtils.degToRad(8),-Math.PI/2,Math.PI/2);
    const difference=Math.atan2(Math.sin(target-authored),Math.cos(target-authored));
    const limit=aim?.limit??1.13;
    // A directed startup can pass the antipode while its source clips blend.
    // Track the final inclination on the branch nearest the last rendered pose,
    // rather than multiplying a freshly wrapped +/-pi correction by clip weight.
    const wanted=aim?.continuous
      ?this.angle+Math.atan2(Math.sin(target-authored-this.angle),Math.cos(target-authored-this.angle))*T.MathUtils.clamp(weight,0,1)
      :T.MathUtils.clamp(difference,-limit,limit)*T.MathUtils.clamp(weight,0,1);
    this.angle+=(wanted-this.angle)*(1-Math.exp(-16*Math.max(0,dt)));
    this.pitch=Math.atan2(Math.sin(authored+this.angle),Math.cos(authored+this.angle));
    if(Math.abs(this.angle)<1e-5)return;
    this.axis.set(1,0,0).applyQuaternion(this.frame);
    this.delta.setFromAxisAngle(this.axis,this.angle);
    pelvis.getWorldQuaternion(this.world).premultiply(this.delta);
    pelvis.parent.getWorldQuaternion(this.parent).invert();
    pelvis.quaternion.copy(this.parent).multiply(this.world);
    pelvis.updateWorldMatrix(false,true);
  }
}
